import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import _ from "lodash";

import MetadataFeed from "./MetadataFeed";
import MarkerPanel from "./MarkerPanel";
import {
    deleteSnappingValidation,
    getSessionData,
    featureOverlaySetDatum,
    validateSnapping,
    featureOverlayAddFeature,
    getRouteSystemCoords,
    featureOverlayClearFeatures,
    setUserPreference,
    fetchWhatThreeWords,
    customAudit,
    getSessionWeatherData,
    getClosestAssets,
    setClosestAssets,
    setDisplayNearbyAssets,
    setCurrentNearbyAsset,
    routeSelected,
    getNetworkTrackResponsibility,
    clearWeatherData,
} from "redux/actions/index";
import { getAbsoluteTimestamp, findMostRecentMetadata, findEnvironmentalData } from "../util/PlaylistUtils";
import { Link } from "react-router-dom";
import {
    calculateELREstimates,
    calculateBearingText,
    coordinateToLatLon,
    coordinateToOSGB,
    convertSpeed,
    coordinateToIrishEasting,
    parseRoutePositionData,
    calculateMileageIncreasing,
} from "../util/Geometry";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faClipboard, faCog } from "@fortawesome/free-solid-svg-icons";
import {
    faInfoCircle,
    faMountain,
    faCompass,
    faTachometerFast,
    faTemperatureHigh,
    faHumidity,
    faCloudRain,
    faWindsock,
    faSyncAlt,
    faHome,
    faChevronLeft,
    faChevronRight,
} from "@fortawesome/pro-regular-svg-icons";
import { faCompass as faCompassSolid } from "@fortawesome/pro-solid-svg-icons";
import { sticky } from "tippy.js";
import UnitSelectionModal from "../UnitSelectionModal";
import RouteCoordinateSystems from "../util/RouteCoordinateSystems";
import { LazyTippy } from "../util/LazyTooltips";
import { Spin, Button, Icon, Collapse, Popover, Select } from "antd";
import mwvLogo from "../../images/MWV-logo-small.png";
import { convertToTimezone } from "../util/TimezoneUtils";
import Tippy from "@tippyjs/react";
import GenericDialog from "components/GenericDialog";
import OBCSpinner from "components/util/OBC";
import { openWidget } from "redux/actions/widgetActions";
import { calculateDistanceInPlaylist, coordinateToTimestampV2 } from "components/util/GeometryUtilsV2";
import { routeGeomSelector, weatherCoordsSelector, weatherDataSelector, weatherRequestedTimestampSelector } from "redux/selectors";

const CHAINS_TO_METRES = 20.1186;
export const ACCESS_FLAGGED_GEOMETRY = "access_flagged_geometry";

const routeIDSelector = (state) => state.playlist.data.routeID;
const routeDetailsSelector = (state) => _.get(state.sessions, [state.playlist.data.routeID], null);
const fullscreenSelector = (state) => state.fullscreen;
const openInfoPanelsSelector = (state) => state.userPreferences.openInfoPanels;
const routeSystemIDSelector = (state) => state.playlist.data.system_id;
const displayNearbyAssetsSelector = (state) => state.assets.displayNearbyAssets;
const currentNearbyAssetSelector = (state) => state.assets.currentNearbyAsset;
const sidekickTypesSelector = (state) => {
    const currentDashboard = _.find(state.dashboards, (dash) => dash.access_token === state.access_token);
    return _.get(currentDashboard, ["config", "sidekick_widget_types"], []);
};
const sessionTagsSelector = (state) => {
    const currentSession = _.get(state.sessions, state.playlist.data.routeID);
    return _.get(currentSession, "tags");
};
const absoluteTimestampSelector = (state) => {
    let absolutePlaylistTimestamp = getAbsoluteTimestamp(state.playlist) * 1000;
    if (!absolutePlaylistTimestamp && state.railInspection.railInspectionImages.data?.length && !state.playlist.video?.length) {
        const currentIndex = _.get(state.playlist, ["position", "currentIndex"]);
        if (currentIndex) {
            absolutePlaylistTimestamp = _.get(state.railInspection.railInspectionImages.data, [currentIndex, "timestamp"], 0);
        }
    }

    return absolutePlaylistTimestamp;
};
const dashboardSelector = (state) => _.find(state.dashboards, (dash) => dash.access_token === state.access_token);
const environmentDataSelector = (state) => state.sessionEnvironmentData;
const currentRoutePositionSelector = (state) => state.playlist.position.routePosition;

const routeCoordinateSelector = (state) => {
    let playlistCoords = state.playlist.position.coords;
    if (playlistCoords && playlistCoords[0] !== null && playlistCoords[1] !== null && !_.isEmpty(playlistCoords)) {
        return playlistCoords;
    } else {
        return null;
    }
};

const nearestAssetsSelector = (state) => state.assets.nearestAssets;
const datumSelector = (state) => state.featureOverlay.datum;
const snappingDataSelector = (state) => state.playlist.data.snapping_data;
const routeLocationsV2Selector = (state) => state.playlist.data.route_locations_v2;
const sourceIndexSelector = (state) => state.playlist.position.sourceIndex;
const cameraOffsetsSelector = (state) => state.playlist.data.offsets;
const currentSessionTagsSelector = (state) => _.get(state.sessions[state.playlist.data.routeID], "tags", []);

const { Panel } = Collapse;
const { Option, OptGroup } = Select;

const PRE_FILL_MESSAGES = {
    other: null,
    dirty_lens: "Lens is not clean",
    obscured: "Footage is obscured by other object (train, wiper, cables, etc)",
    wrong_track: "Wrong track detected",
    camera_not_working: "Camera not working properly or not at all",
    too_dark_bright: "Recording is too bright or too dark",
    blurry: "Recording is blurry",
    sensitive_footage: "The video contains footage that should be removed from general availability immediately",
};

const RouteTitleDetails = () => {
    const dispatch = useDispatch();
    const [reportProblemVisible, setReportProblemVisible] = useState();
    const [sessionIdCopyText, setSessionIdCopyText] = useState("Click to copy");
    const [sessionIdHovered, setSessionIdHovered] = useState(false);
    const [preFilledMessage, setPreFilledMessage] = useState(null);

    const showReportProblemDialog = () => {
        setReportProblemVisible(true);
    };

    const hideReportProblemDialog = () => {
        setReportProblemVisible(false);
        setPreFilledMessage(null);
    };

    const routeID = useSelector(routeIDSelector);
    const routeBeingViewed = useSelector(routeDetailsSelector);
    const currentDashboard = useSelector(currentDashboardSelector);

    const sidekickTypes = useSelector(sidekickTypesSelector);
    const sessionTags = useSelector(sessionTagsSelector);
    const absoluteTimestamp = useSelector(absoluteTimestampSelector);
    const userConfig = useSelector(userConfigSelector);

    useEffect(() => {
        dispatch(getSessionData(routeID));
    }, [dispatch, routeID]);

    const handleSessionIdHovered = () => {
        if (sessionIdCopyText === "Copied!") {
            setSessionIdCopyText("Click to copy");
        }

        setSessionIdHovered(false);
    };

    const handleSessionIdClick = (e, id) => {
        navigator.clipboard.writeText(id);
        setSessionIdCopyText("Copied!");
    };

    let analyticsLink = null;
    let railInspectionLink = null;

    if (routeBeingViewed && routeBeingViewed.inspection && _.get(currentDashboard, ["config", "rail_inspection_enabled"])) {
        const linkData = `rail-inspection/${routeID}${absoluteTimestamp ? "/" + absoluteTimestamp : ""}`;

        railInspectionLink = (
            <Popover
                overlayClassName="PopoverContextMenuContainer"
                trigger="contextMenu"
                destroyTooltipOnHide
                content={
                    <div className="PopoverContextMenu">
                        <Button
                            type="primary"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                window.open(`${window.location.href}${linkData}`, "_blank", "noreferrer");
                            }}>
                            Open in New Tab
                        </Button>
                    </div>
                }>
                <Link to={"/" + linkData}>
                    <button
                        id="RailInspectionRouteInfoButton"
                        className="ConfirmActionButton blue">
                        Rail Inspection
                    </button>
                </Link>
            </Popover>
        );
    }

    const flaggedAccess = useMemo(() => {
        const accessEnabled = _.get(currentDashboard, ["config", ACCESS_FLAGGED_GEOMETRY], false);
        const superAdmin = userConfig.super_admin;

        return accessEnabled || superAdmin;
    }, [currentDashboard, userConfig]);

    const trackGeometryButton = useMemo(() => {
        if (sidekickTypes && sessionTags) {
            if (sidekickTypes.includes("Track Geometry V2") || sidekickTypes.includes("Track Geometry")) {
                if (sessionTags.includes("Geometry Flagged")) {
                    return (
                        <Tippy
                            placement="top"
                            content={
                                "Geometry data has been recorded for this session, but is unavailable due to vehicle maintenance or data quality validation checks"
                            }
                            theme="aivrlight"
                            zIndex="600">
                            <span style={{ marginLeft: 7 }}>
                                <button
                                    disabled={!flaggedAccess}
                                    onClick={() => {
                                        if (!flaggedAccess) {
                                            return;
                                        }
                                        dispatch(openWidget("track-geom-v2"));
                                    }}
                                    className={`ConfirmActionButton blue ${!flaggedAccess ? "Disabled" : ""}`}>
                                    Track Geometry
                                </button>
                            </span>
                        </Tippy>
                    );
                } else if (sessionTags.includes("Track Geometry") || sessionTags.includes("Track Geometry V2")) {
                    return (
                        <button
                            className="ConfirmActionButton blue"
                            onClick={() => dispatch(openWidget("track-geom-v2"))}>
                            Track Geometry
                        </button>
                    );
                }
            }
        }
        return null;
    }, [dispatch, sessionTags, sidekickTypes, userConfig]);

    const oleGeometryButton = useMemo(() => {
        if (sidekickTypes && sessionTags) {
            if (sidekickTypes.includes("OLE Geometry") && sessionTags.includes("OLE Geometry")) {
                return (
                    <button
                        className="ConfirmActionButton blue"
                        onClick={() => dispatch(openWidget("ole-geometry"))}>
                        OLE Geometry
                    </button>
                );
            }
        }
        return null;
    }, [dispatch, sessionTags, sidekickTypes]);

    const shoeConductorGeometryButton = useMemo(() => {
        if (sidekickTypes && sessionTags) {
            if (sidekickTypes.includes("Conductor Shoe Geometry") && sessionTags.includes("Conductor Shoe Geometry")) {
                return (
                    <button
                        className="ConfirmActionButton blue"
                        onClick={() => dispatch(openWidget("conductor-shoe-geometry"))}>
                        Conductor Shoe Geometry
                    </button>
                );
            }
        }
        return null;
    }, [dispatch, sessionTags, sidekickTypes]);

    const stillsWidgetsButton = useMemo(() => {
        if (routeBeingViewed && routeBeingViewed.stream_info.some((stream) => stream.source !== undefined) && currentDashboard && currentDashboard.isBeta) {
            return (
                <button
                    className="ConfirmActionButton blue"
                    onClick={() => {
                        routeBeingViewed.stream_info.forEach((stream, index, array) => {
                            const offset = (array.length - index) * 30;
                            dispatch(
                                openWidget("stills", {
                                    features: `width=600,height=400,left=${196 - offset},top=${-432 - offset}`,
                                    queryString: `source=${stream.source}`,
                                }),
                            );
                        });
                    }}>
                    Open sources as windows
                </button>
            );
        }

        return null;
    }, [currentDashboard, dispatch, routeBeingViewed]);

    const selectPrefillMessage = (value) => {
        if (value !== "other") {
            setPreFilledMessage(`[${value}] - ${PRE_FILL_MESSAGES[value]}`);
        } else {
            setPreFilledMessage(null);
        }
    };

    const reportProblemWithSessionContent = useMemo(() => {
        let content = (
            <>
                <div>
                    Use this form to report an issue with this session. For example video not aligning with location, incorrect location or bad footage etc. or
                    select relevant issue from the list below.
                </div>

                <Select
                    defaultValue="other"
                    style={{ width: "100%", marginTop: "3px" }}
                    onChange={selectPrefillMessage}>
                    <Option value="other">Other</Option>
                    <OptGroup label="Common issues">
                        <Option value="blurry">Blurry</Option>
                        <Option value="camera_not_working">Camera not working</Option>
                        <Option value="dirty_lens">Dirty lens</Option>
                        <Option value="obscured">Obscured</Option>
                        <Option value="sensitive_footage">Sensitive Footage</Option>
                        <Option value="too_dark_bright">Too dark/bright</Option>
                        <Option value="wrong_track">Wrong track</Option>
                    </OptGroup>
                </Select>
            </>
        );

        return content;
    }, []);

    return (
        <>
            <div className="RouteInfo-Viewing">
                <span className="RouteInfo-Label">Viewing</span>
                <div className="RouteInfo-Text-Container">
                    {routeBeingViewed && (
                        <div style={{ display: "flex", alignItems: "center", padding: "5px 0" }}>
                            <Tippy
                                hideOnClick={false}
                                placement="top"
                                content={sessionIdCopyText}
                                onHidden={() => {
                                    handleSessionIdHovered();
                                }}
                                theme="aivrlight"
                                zIndex="600">
                                <span
                                    onClick={(e) => handleSessionIdClick(e, routeID)}
                                    style={{
                                        padding: 5,
                                        fontSize: 12,
                                        background: sessionIdHovered ? "rgba(109, 67, 223, 0.6)" : "rgba(109, 67, 223, 0.4)",
                                        borderRadius: 2,
                                        marginRight: 7.5,
                                        letterSpacing: 0.5,
                                        fontWeight: "bold",
                                        cursor: "pointer",
                                    }}>
                                    #{routeID}
                                </span>
                            </Tippy>

                            <span
                                style={{ fontWeight: "bold" }}
                                id="selected-session-name">
                                {routeBeingViewed ? routeBeingViewed.route_name : "No Route Selected"}
                            </span>
                            <Tippy
                                placement="top"
                                content="Report a problem with this session"
                                theme="aivrlight"
                                zIndex="600">
                                <div>
                                    <Button
                                        className="RouteInfo-Button"
                                        onClick={showReportProblemDialog}>
                                        <Icon
                                            type="bug"
                                            theme="outlined"
                                        />
                                    </Button>
                                </div>
                            </Tippy>
                        </div>
                    )}
                    <div className="ConfirmActionButton-Container">
                        {analyticsLink}
                        {railInspectionLink}
                        {trackGeometryButton}
                        {oleGeometryButton}
                        {stillsWidgetsButton}
                        {shoeConductorGeometryButton}
                    </div>
                </div>
            </div>
            {reportProblemVisible && (
                <GenericDialog
                    title="Report problem"
                    onClose={hideReportProblemDialog}
                    placeholder={`Problem with session ${routeID}`}
                    promptText={reportProblemWithSessionContent}
                    reportOrigin="session-info"
                    preFilledMessage={preFilledMessage}
                />
            )}
        </>
    );
};

const EMPTY_ARRAY = [];

const timestampSelector = (state) => {
    const routeID = state.playlist.data.routeID;
    const isVideo = state.playlist.position.isVideo;
    const sourceIndex = state.playlist.position.sourceIndex;
    const playlist = isVideo ? _.get(state.playlist.data, ["video", sourceIndex], EMPTY_ARRAY) : state.playlist.data.image;
    const index = state.playlist.position.currentIndex;
    const offset = state.playlist.position.currentTimeOffset || 0;
    return getAbsoluteTimestamp(routeID, playlist, index, isVideo, offset);
};

const routeIsLoadedSelector = (state) => {
    const routeID = state.playlist.data.routeID;
    const isVideo = state.playlist.position.isVideo;
    const sourceIndex = state.playlist.position.sourceIndex;
    const playlist = isVideo ? _.get(state.playlist.data, ["video", sourceIndex], EMPTY_ARRAY) : state.playlist.data.image;
    const index = state.playlist.position.currentIndex;
    return routeID && playlist && index !== -1;
};

const altitudeSelector = (state) => {
    return _.get(state.routeMetadata, ["ALTITUDE", state.playlist.data.routeID], EMPTY_ARRAY);
};

const humidityAndTempSelector = (state) => {
    return _.get(state.routeMetadata, ["EZO_HUM_STATUS", state.playlist.data.routeID], EMPTY_ARRAY);
};

const tachoDataSelector = (state) => _.get(state.routeMetadata, ["TACHO_DATA", state.playlist.data.routeID], EMPTY_ARRAY);
const speedAndBearingSelector = (state) => _.get(state.routeMetadata, ["SPEED_AND_BEARING", state.playlist.data.routeID], EMPTY_ARRAY);

export const PositionalInfo = ({ hud = false }) => {
    const timestamp = useSelector(timestampSelector);
    const altMetadata = useSelector(altitudeSelector);
    const speedMetadata = useSelector(speedAndBearingSelector);
    const tachoMetadata = useSelector(tachoDataSelector);
    const humidityAndTempMetadata = useSelector(humidityAndTempSelector);
    const userConfig = useSelector(userConfigSelector);
    const videolessTimestamp = useSelector(videolessTimestampSelector);

    let altitude = "Unknown";
    let speed = "Unknown";
    let speedSource = "";
    let bearing = "Unknown";
    let temperature = null;
    let humidity = null;
    let timestampSeconds = videolessTimestamp ? videolessTimestamp / 1000 : timestamp;

    if (timestampSeconds) {
        const altitudeInfo = findMostRecentMetadata(altMetadata, timestampSeconds);
        if (altitudeInfo) {
            altitude = Math.round(altitudeInfo.data) + "m";
        }
        const tachoData = findMostRecentMetadata(tachoMetadata, timestampSeconds);
        let baseSpeed = null;
        if (!_.isNil(tachoData?.data.speed) && tachoData.timestamp > timestampSeconds - 5) {
            baseSpeed = tachoData?.data.speed;
            speedSource = " (Tachometer)";
        }
        const speedInfo = findMostRecentMetadata(speedMetadata, timestampSeconds);
        if (baseSpeed == null && !_.isNil(speedInfo?.data.speed) && speedInfo.timestamp > timestampSeconds - 5) {
            baseSpeed = speedInfo?.data.speed;
            speedSource = " (GPS)";
        }
        if (!_.isNil(speedInfo?.data.bearing)) {
            bearing = calculateBearingText(speedInfo.data.bearing);
        }
        if (baseSpeed != null) {
            let unitDict = {
                metres: "m/s",
                miles: "MPH",
                kilometers: "KPH",
            };
            let unit = userConfig.speed_units || "miles";
            speed = Math.round(convertSpeed(baseSpeed, unit)) + " " + unitDict[unit] + speedSource;
        }
        const humidityAndTempInfo = findMostRecentMetadata(humidityAndTempMetadata, timestampSeconds);
        if (!_.isNil(humidityAndTempInfo?.data.temperature)) {
            temperature = Number(humidityAndTempInfo.data.temperature).toFixed(2) + "°C";
        }
        if (!_.isNil(humidityAndTempInfo?.data.humidity)) {
            humidity = Number(humidityAndTempInfo.data.humidity).toFixed(2) + "%";
        }
    }

    return (
        <MetadataFeed
            items={["ALTITUDE", "SPEED_AND_BEARING", "TACHO_DATA", "EZO_HUM_STATUS"]}
            timestamp={timestampSeconds || null}>
            <div className={hud ? "HUD-RouteInfo-Row" : "RouteInfo-Row"}>
                <div className={hud ? "HUD-RouteInfo-Altitude" : "RouteInfo-Altitude"}>
                    <div className={hud ? "HUD-RouteInfo-Text-Container" : "RouteInfo-Text-Container"}>
                        <Tippy
                            arrow={true}
                            placement="top"
                            content="Speed"
                            theme="aivrlight"
                            zIndex="600">
                            <div className={hud ? "HUD-RouteInfo-BlockItem" : "RouteInfo-BlockItem"}>
                                <FontAwesomeIcon icon={faTachometerFast} />
                                <span className="secondary">{speed ? speed : "No Route Selected"}</span>
                            </div>
                        </Tippy>
                    </div>
                </div>
                <div className={hud ? "HUD-RouteInfo-Altitude" : "RouteInfo-Altitude"}>
                    <div className={hud ? "HUD-RouteInfo-Text-Container" : "RouteInfo-Text-Container"}>
                        <Tippy
                            placement="top"
                            content="Bearing"
                            theme="aivrlight"
                            zIndex="600">
                            <div className={hud ? "HUD-RouteInfo-BlockItem" : "RouteInfo-BlockItem"}>
                                <FontAwesomeIcon icon={faCompass} />
                                <span className="secondary">{bearing ? bearing : "No Route Selected"}</span>
                            </div>
                        </Tippy>
                    </div>
                </div>
                <div className={hud ? "HUD-RouteInfo-Altitude" : "RouteInfo-Altitude"}>
                    <div className={hud ? "HUD-RouteInfo-Text-Container" : "RouteInfo-Text-Container"}>
                        <Tippy
                            placement="top"
                            content="Altitude"
                            theme="aivrlight"
                            zIndex="600">
                            <div className={hud ? "HUD-RouteInfo-BlockItem" : "RouteInfo-BlockItem"}>
                                <FontAwesomeIcon icon={faMountain} />
                                <span className="secondary">{altitude ? altitude : "No Route Selected"}</span>
                            </div>
                        </Tippy>
                    </div>
                </div>
            </div>
            <div className={temperature ? "HUD-RouteInfo-Row" : "HUD-RouteInfo-Row disabled"}>
                <div className="HUD-RouteInfo-Altitude">
                    <div className="HUD-RouteInfo-Text-Container">
                        <Tippy
                            placement="top"
                            content="Temperature"
                            theme="aivrlight"
                            zIndex="600">
                            <div className={`HUD-RouteInfo-BlockItem ${temperature}`}>
                                <FontAwesomeIcon icon={faTemperatureHigh} />
                                <span className="secondary">{temperature}</span>
                            </div>
                        </Tippy>
                    </div>
                </div>
                <div className="HUD-RouteInfo-Altitude">
                    <div className="HUD-RouteInfo-Text-Container">
                        <Tippy
                            placement="top"
                            content="Humidity"
                            theme="aivrlight"
                            zIndex="600">
                            <div className={`HUD-RouteInfo-BlockItem ${humidity}`}>
                                <FontAwesomeIcon icon={faHumidity} />
                                <span className="secondary">{humidity}</span>
                            </div>
                        </Tippy>
                    </div>
                </div>
            </div>
        </MetadataFeed>
    );
};

function WhatThreeWordsCopier({ words }) {
    return (
        <LazyTippy
            placement="top"
            sticky={true}
            plugins={[sticky]}
            arrow={true}
            theme="aivrlight"
            interactive="true"
            content="Copy what3words data to clipboard">
            <div
                className="CopyToClipboard"
                onClick={() => {
                    navigator.clipboard.writeText(words);
                }}>
                <FontAwesomeIcon icon={faClipboard} />
            </div>
        </LazyTippy>
    );
}

function LatLonCopier() {
    const store = useStore();
    return (
        <LazyTippy
            placement="top"
            sticky={true}
            plugins={[sticky]}
            arrow={true}
            theme="aivrlight"
            interactive="true"
            content="Copy Lat / Lon data to clipboard">
            <div
                className="CopyToClipboard"
                onClick={() => {
                    const coordinate = routeCoordinateSelector(store.getState());
                    console.log("debug copying coords", coordinate);

                    navigator.clipboard.writeText(coordinateToLatLon(coordinate));
                }}>
                <FontAwesomeIcon icon={faClipboard} />
            </div>
        </LazyTippy>
    );
}

function OsgbCopier({ eastingNorthing }) {
    return (
        <LazyTippy
            placement="top"
            sticky={true}
            plugins={[sticky]}
            arrow={true}
            theme="aivrlight"
            interactive="true"
            content="Copy Easting / Northing data to clipboard">
            <div
                className="CopyToClipboard"
                onClick={() => {
                    navigator.clipboard.writeText(eastingNorthing);
                }}>
                <FontAwesomeIcon icon={faClipboard} />
            </div>
        </LazyTippy>
    );
}

const headingSelector = (state) => {
    return state.playlist.position.heading;
};

export const WhatThreeWords = ({ hud = false }) => {
    const [whatThreeWords, setWhatThreeWords] = useState("");
    const dispatch = useDispatch();

    const currentCoordinate = useSelector(routeCoordinateSelector);
    const playerState = useSelector(playerStateSelector);

    const getWhatThreeWords = useCallback(() => {
        dispatch(fetchWhatThreeWords(currentCoordinate[1], currentCoordinate[0])).then((words) => {
            setWhatThreeWords(words);
        });
    }, [dispatch, currentCoordinate]);

    useEffect(() => {
        setWhatThreeWords("");
    }, [currentCoordinate]);

    const content = useMemo(() => {
        if (!currentCoordinate) {
            return <p className="RouteInfoMsg">No Route Selected</p>;
        } else if (playerState === "playing") {
            return <p className="RouteInfoMsg">Pause video for W3W</p>;
        } else if (!whatThreeWords) {
            return hud ? (
                <div
                    onClick={getWhatThreeWords}
                    className="OverlayButton active">
                    Get W3W
                </div>
            ) : (
                <Button
                    onClick={getWhatThreeWords}
                    className="WhatThreeWordsButton">
                    Get what3words
                </Button>
            );
        } else {
            return (
                <div className={hud ? "HUD-RouteInfoELR__Container" : "RouteInfoELR__Container"}>
                    <p style={{ marginRight: 7.5, marginBottom: 0 }}>{whatThreeWords}</p>
                    <WhatThreeWordsCopier words={whatThreeWords} />
                </div>
            );
        }
    }, [playerState, getWhatThreeWords, whatThreeWords, currentCoordinate]);

    return (
        <div className={hud ? "W3W-Container" : "RouteInfo-Altitude"}>
            <span className={hud ? "" : "RouteInfo-Label"}>what3words</span>
            <div className={hud ? "HUD-RouteInfo-Text-Container" : "RouteInfo-Text-Container"}>
                <span className="W3WValue">{content}</span>
            </div>
        </div>
    );
};

const playlistSelector = (state) => state.playlist;

export const LatLonInfo = ({ coordSystem, hud = false }) => {
    const currentTimestamp = useSelector(absoluteTimestampSelector);
    const routeIsLoaded = useSelector(routeIsLoadedSelector);
    const currentCoordinate = useSelector(routeCoordinateSelector);
    const playerState = useSelector(playerStateSelector);
    const nearestAssets = useSelector(nearestAssetsSelector);
    const routeSystemID = useSelector(routeSystemIDSelector);
    const sessionID = useSelector(routeIDSelector);
    const displayNearbyAssets = useSelector(displayNearbyAssetsSelector);
    const currentNearbyAsset = useSelector(currentNearbyAssetSelector);
    const dashboard = useSelector(dashboardSelector);
    const currentRoutePosition = useSelector(currentRoutePositionSelector);
    const routeBeingViewed = useSelector(routeDetailsSelector);
    const playlist = useSelector(playlistSelector);

    const [closestLocation, setClosestLocation] = useState({});
    const [assetError, setAssetError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setDisplayNearbyAssets(false));
        dispatch(setClosestAssets([]));
    }, [sessionID, dispatch]);

    useEffect(() => {
        const handleClick = (e) => {
            const availableAssetGroups = _.get(dashboard, ["config", "asset_groups"], []);

            const checkList = ["NearbyAssets", "fa-chevron-left", "fa-chevron-right", "AssetInfoNav"];
            const classList = [
                ..._.get(e, "target.classList", []),
                ..._.get(e, "target.parentElement.classList", []),
                ..._.get(e, "target.parentElement.parentElement.classList", []),
                ..._.get(e, "target.parentElement.parentElement.parentElement.classList", []),
                ..._.get(e, "target.parentElement.parentElement.parentElement.parentElement.classList", []),
                _.get(e, "target.nearestViewportElement.id", ""),
            ];

            if (!availableAssetGroups.includes(_.get(currentNearbyAsset, "asset_group_id", "").toString())) {
                checkList.push("leaflet-marker-icon");
            }

            if (!_.intersection(checkList, classList).length) {
                if (displayNearbyAssets) {
                    dispatch(setDisplayNearbyAssets(false));
                    dispatch(setClosestAssets([]));
                    dispatch(setCurrentNearbyAsset(null));
                }
            }
        };

        document.addEventListener("click", handleClick);

        return () => {
            document.removeEventListener("click", handleClick);
        };
    });

    let latLong = useMemo(() => {
        if (routeIsLoaded) {
            return coordinateToLatLon(currentCoordinate);
        } else {
            return null;
        }
    }, [routeIsLoaded, currentCoordinate]);

    let osgb = useMemo(() => {
        if (routeIsLoaded) {
            let coord;
            if (routeSystemID === "Irish Rail") {
                coord = coordinateToIrishEasting(currentCoordinate);
            } else if (routeSystemID === "ELR Mile & Chain") {
                coord = coordinateToOSGB(currentCoordinate);
            }
            return coord;
        } else {
            return null;
        }
    }, [routeIsLoaded, routeSystemID, currentCoordinate]);

    const latLonCopier = useMemo(() => {
        return <LatLonCopier />;
    }, []);

    const osgbCopier = useMemo(() => {
        return <OsgbCopier eastingNorthing={osgb} />;
    }, [osgb]);

    const latLonDisplay = useMemo(() => {
        if (latLong) {
            if (hud) {
                const lat = latLong.split(",")[0];
                const long = latLong.split(",")[1].replace(" ", "");

                return (
                    <>
                        <span>{lat}</span>
                        <span>{long}</span>
                    </>
                );
            } else {
                return (
                    <>
                        <span>{latLong}</span>
                        {latLonCopier}
                    </>
                );
            }
        } else {
            return "No Route Selected";
        }
    }, [latLong, latLonCopier, hud]);

    const googleMapsUrl = useMemo(() => {
        let url = null;
        if (latLong) {
            const lat = latLong.split(",")[0];
            const lng = latLong.split(",")[1].trim();
            if (lat && lng) {
                url = `https://www.google.com/maps?q=${lat},${lng}`;
            }
        }
        return url;
    }, [latLong]);

    const osgbDisplay = useMemo(() => {
        if (osgb) {
            if (hud) {
                const easting = osgb.split(",")[0];
                const northing = osgb.split(",")[1].replace(" ", "");

                return (
                    <>
                        <span>{easting}</span>
                        <span>{northing}</span>
                    </>
                );
            } else {
                return (
                    <>
                        <span>{osgb}</span>
                        {osgbCopier}
                    </>
                );
            }
        } else {
            return "No Route Selected";
        }
    }, [osgb, hud]);

    const nearbyAssets = useMemo(() => {
        if (!_.get(dashboard, ["config", "asset_groups", "length"], false)) return null;

        let nearestLocation = {};

        if (currentTimestamp && currentRoutePosition && !_.isEmpty(currentRoutePosition)) {
            nearestLocation = parseRoutePositionData(currentRoutePosition, routeSystemID);
            setClosestLocation(nearestLocation);
        }

        const fetchClosestAssets = () => {
            dispatch(setDisplayNearbyAssets(true));
            setAssetError(false);

            if (nearestLocation && nearestLocation.hasOwnProperty("elr")) {
                setIsLoading(true);
                dispatch(
                    getClosestAssets(
                        nearestLocation.elr,
                        nearestLocation.position,
                        () => {
                            setIsLoading(false);
                        },
                        () => {
                            setIsLoading(false);
                            setAssetError(true);
                        },
                    ),
                );
            } else {
                setAssetError(true);
                console.log("Cannot get current chains location");
            }
        };

        if (!osgb) {
            return <p className="RouteInfoMsg">No Route Selected</p>;
        } else if (playerState === "playing") {
            return <p className="RouteInfoMsg">Pause the video to calculate current position</p>;
        } else {
            return displayNearbyAssets ? (
                <Button
                    className="RevealNearbyAssets"
                    onClick={() => {
                        dispatch(setDisplayNearbyAssets(false));
                        dispatch(setClosestAssets([]));
                        dispatch(setCurrentNearbyAsset({}));
                    }}>
                    Close
                </Button>
            ) : (
                <Button
                    className="RevealNearbyAssets"
                    onClick={() => {
                        fetchClosestAssets();
                    }}>
                    Get nearby assets
                </Button>
            );
        }
    }, [dashboard, currentTimestamp, currentRoutePosition, osgb, playerState, routeSystemID, dispatch, displayNearbyAssets]);

    const nearbyAssetIndex = useMemo(() => {
        return _.findIndex(nearestAssets, (asset) => asset.id === currentNearbyAsset.id);
    }, [nearestAssets, currentNearbyAsset]);

    const navigateAssets = useCallback(
        (direction) => {
            if (direction === "left") {
                dispatch(setCurrentNearbyAsset(nearestAssets[nearbyAssetIndex - 1]));
            } else {
                dispatch(setCurrentNearbyAsset(nearestAssets[nearbyAssetIndex + 1]));
            }
        },
        [nearestAssets, nearbyAssetIndex, dispatch],
    );

    const nearbyAssetContent = useMemo(() => {
        if (isLoading) {
            return (
                <div className="NearbyAssetsInfoContainer">
                    <OBCSpinner
                        size={40}
                        color="#e8dfff"
                    />
                </div>
            );
        }

        if (assetError) {
            return (
                <div className="NearbyAssetsInfoContainer">
                    <span>Unable to retrieve nearby assets for this route or location</span>
                </div>
            );
        }

        if (nearestAssets.length === 0) {
            return (
                <div className="NearbyAssetsInfoContainer">
                    <span>No assets found near this route or location</span>
                </div>
            );
        }

        if (!_.isEmpty(currentNearbyAsset)) {
            let id = "Unknown";
            if (currentNearbyAsset.consensus_data) {
                if (currentNearbyAsset.consensus_data.hasOwnProperty("structure_id")) {
                    id = currentNearbyAsset.consensus_data.structure_id;
                } else if (currentNearbyAsset.consensus_data.hasOwnProperty("signal_id")) {
                    id = currentNearbyAsset.consensus_data.signal_id;
                }

                if (id !== "Unknown") {
                    id = id.replace(/,/g, "/");
                }
            }

            let assetType = null;
            if (currentNearbyAsset.summary_data) {
                if (currentNearbyAsset.summary_data.match_count > 0 && currentNearbyAsset.summary_data.external_match_count > 0) {
                    assetType = "AIVR and External";
                } else if (currentNearbyAsset.summary_data.match_count > 0 && !currentNearbyAsset.summary_data.external_match_count) {
                    assetType = "AIVR";
                } else if (currentNearbyAsset.summary_data.match_count === 0 && currentNearbyAsset.summary_data.external_match_count > 0) {
                    assetType = "External";
                }
            }

            let thumbnail = null;
            const images = _.get(currentNearbyAsset, ["summary_data", "images"], {});
            if (images.hasOwnProperty("thumbnail")) {
                thumbnail = images.thumbnail;
            }

            let description = null;
            const summaryData = _.get(currentNearbyAsset, "summary_data", {});
            if (summaryData.hasOwnProperty("external_descriptions")) {
                description = summaryData.external_descriptions;
            }

            const chainsToMetres = (chains) => {
                if (closestLocation) {
                    const isSessionBackward = routeBeingViewed.tags?.includes("Backward") ?? false;
                    const isMileageIncreasing = calculateMileageIncreasing(
                        playlist.position.playlistDistance,
                        playlist.data.route_locations_v2,
                        isSessionBackward,
                    );
                    const chainsDistance = chains - closestLocation.position;
                    const deltaInMetres = Math.floor(chainsDistance * CHAINS_TO_METRES) * (isMileageIncreasing ? 1 : -1);

                    return `${Math.abs(deltaInMetres)}m ${deltaInMetres === 0 ? "(this location)" : deltaInMetres > 0 ? "ahead" : "behind"}`;
                }

                return `${chains} (chains)`;
            };

            const formatLineReference = (chains) => {
                let miles = Math.floor(chains / 80);
                let yards = Math.floor((chains - miles * 80) * 22);
                let elr = _.get(currentNearbyAsset, "route_position_elr");
                let trid = _.get(currentNearbyAsset, "route_position_trid");
                return `${elr} ${miles}m ${yards}y ${trid}`;
            };

            return (
                <div className="NearbyAssetContainer">
                    <div className="NearbyAssets">
                        <div className="NearbyAssetsContent">
                            {thumbnail && (
                                <img
                                    src={thumbnail}
                                    alt="Asset thumbnail"
                                    className="NearbyAssetsContentImg"
                                />
                            )}
                            <div className="NearbyAssetsInfoContainer">
                                <div className="AssetInfoRow">
                                    <span className="NearbyAssetsContentLabel">Asset type</span>
                                    <span className="NearbyAssetsContentValue">{currentNearbyAsset.asset_class}</span>
                                </div>
                                <div className="AssetInfoRow">
                                    <span className="NearbyAssetsContentLabel">ID</span>
                                    <span className="NearbyAssetsContentValue">{id}</span>
                                </div>
                                <div className="AssetInfoRow">
                                    <span className="NearbyAssetsContentLabel">Distance</span>
                                    <span className="NearbyAssetsContentValue">{chainsToMetres(currentNearbyAsset.route_position_chains)}</span>
                                </div>
                                <div className="AssetInfoRow">
                                    <span className="NearbyAssetsContentLabel">Line reference</span>
                                    <span className="NearbyAssetsContentValue">{formatLineReference(currentNearbyAsset.route_position_chains)}</span>
                                </div>
                                {assetType && (
                                    <div className="AssetInfoRow">
                                        <span className="NearbyAssetsContentLabel">Type</span>
                                        <span className="NearbyAssetsContentValue">{assetType}</span>
                                    </div>
                                )}
                                {description && (
                                    <div className="AssetInfoRow">
                                        <span className="NearbyAssetsContentLabel">Description</span>
                                        <span className="NearbyAssetsContentValue">{description[0]}</span>
                                    </div>
                                )}
                            </div>
                            <div className="AssetInfoNav">
                                <button
                                    type="button"
                                    className={`${nearbyAssetIndex === 0 && "Disabled"}`}
                                    disabled={nearbyAssetIndex === 0}
                                    onClick={() => navigateAssets("left")}>
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                </button>
                                <div className="AssetInfoNavPagination">
                                    <span
                                        className="text-right"
                                        style={{ width: String(nearestAssets.length).length * 10 }}>
                                        {nearbyAssetIndex + 1}
                                    </span>{" "}
                                    of <span style={{ width: String(nearestAssets.length).length * 10 }}>{nearestAssets.length}</span>
                                </div>
                                <button
                                    type="button"
                                    className={`${nearbyAssetIndex + 1 === nearestAssets.length && "Disabled"}`}
                                    disabled={nearbyAssetIndex + 1 === nearestAssets.length}
                                    onClick={() => navigateAssets("right")}>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    }, [isLoading, assetError, nearestAssets.length, currentNearbyAsset, nearbyAssetIndex, closestLocation, routeBeingViewed, playlist, navigateAssets]);

    const LatLonInfoForHUD = () => {
        return (
            <>
                <div className="HUD-RouteInfo-Inline">
                    {coordSystem === "latlon" ? (
                        <div className="HUD-RouteInfo-RowBlock">
                            <div class="HUD-LatLonLabel">
                                <span className="HUD-RouteInfo-Label">LAT</span>
                                <span className="HUD-RouteInfo-Label">LON</span>
                            </div>
                            <div className="HUD-LatLonDisplay">{latLonDisplay}</div>
                        </div>
                    ) : routeSystemID && coordSystem === "osgb" ? (
                        <div className="HUD-RouteInfo-RowBlock">
                            <div class="HUD-LatLonLabel">
                                <span className="HUD-RouteInfo-Label">EASTING</span>
                                <span className="HUD-RouteInfo-Label">NORTHING</span>
                            </div>
                            <div className="HUD-LatLonDisplay">{osgbDisplay}</div>
                        </div>
                    ) : null}
                </div>
            </>
        );
    };

    const LatLonInfoForMainUI = ({ showAssets }) => {
        return (
            <div className="RouteInfoGrid">
                <div className="RouteInfo-Altitude">
                    <span className="RouteInfo-Label">(LAT, LON)</span>
                    <div className="RouteInfo-Text-Container nowrap">
                        <span className="RouteInfoDisplayWrapper">{latLonDisplay}</span>
                    </div>
                </div>

                {routeSystemID === "ELR Mile & Chain" || routeSystemID === "Irish Rail" ? (
                    <div
                        className="RouteInfo-Altitude"
                        style={{ gridColumn: "2 / span 4" }}>
                        {routeSystemID === "Irish Rail" ? (
                            <Tippy
                                placement="top"
                                content={<span>Irish Grid</span>}
                                theme="aivrlight"
                                zIndex="600">
                                <span className="RouteInfo-Label">(EASTING, NORTHING)</span>
                            </Tippy>
                        ) : (
                            <span className="RouteInfo-Label">(EASTING, NORTHING)</span>
                        )}
                        <div className="RouteInfo-Text-Container nowrap">
                            <span className="RouteInfoDisplayWrapper">{osgbDisplay}</span>
                        </div>
                    </div>
                ) : (
                    <div style={{ gridColumn: "4 / span 4" }}></div>
                )}

                {_.get(dashboard, ["config", "asset_groups", "length"], false) ? (
                    <div className="RouteInfo-Altitude">
                        <span className="RouteInfo-Label">ASSETS</span>
                        <div className="RouteInfo-Text-Container">
                            <Tippy
                                visible={showAssets}
                                maxWidth={400}
                                content={nearbyAssetContent}
                                className="AssetTippy"
                                arrow={false}>
                                <span className="secondary">{nearbyAssets}</span>
                            </Tippy>
                        </div>
                    </div>
                ) : null}

                <div className="RouteInfo-Altitude">
                    <span className="RouteInfo-Label">Google Maps</span>
                    <div className="RouteInfo-Text-Container">
                        {googleMapsUrl ? (
                            <span className="secondary">
                                <Button
                                    style={{ width: "150px", height: "25px" }}
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(`${googleMapsUrl}`, "_blank", "noopener noreferrer");
                                    }}>
                                    Open in new tab
                                </Button>
                            </span>
                        ) : (
                            <span class="secondary">
                                <p class="RouteInfoMsg">No Route Selected</p>
                            </span>
                        )}
                    </div>
                </div>

                <WhatThreeWords />
            </div>
        );
    };

    if (hud) {
        return <LatLonInfoForHUD />;
    } else {
        return <LatLonInfoForMainUI showAssets={displayNearbyAssets} />;
    }
};

export const VideoTimeDisplay = () => {
    const videoTimestamp = useSelector(timestampSelector);
    const userConfig = useSelector(userConfigSelector);
    const [copyTimestampText, setCopyTimestampText] = useState("Click to copy current timestamp");
    const [copiedTimestamp, setCopiedTimestamp] = useState(null);

    const humanTime = useMemo(() => {
        const dpDate = new Date(videoTimestamp * 1000);
        return convertToTimezone(dpDate, userConfig.convert_to_utc);
    }, [videoTimestamp, userConfig.convert_to_utc]);

    const handleCopyTimestampClick = (e) => {
        const timestamp = userConfig.super_admin ? parseInt(videoTimestamp) : humanTime;

        e.preventDefault();
        if (navigator && navigator.clipboard) {
            navigator.clipboard.writeText(timestamp);
            setCopyTimestampText("Copied!");
            setCopiedTimestamp(timestamp);
        }
    };

    // when videoTimestamp changed reset tooltip text
    useEffect(() => {
        if (videoTimestamp !== copiedTimestamp) {
            setCopyTimestampText("Click to copy current timestamp");
        }
    }, [copiedTimestamp, videoTimestamp]);

    return (
        <div className="RouteInfo-Inline">
            <div className="RouteInfo-Altitude">
                <div className="RouteInfo-Text-Container nowrap">
                    <span className="secondary">
                        {videoTimestamp ? (
                            <>
                                {humanTime}
                                <LazyTippy
                                    content={copyTimestampText}
                                    placement="top"
                                    sticky={true}
                                    plugins={[sticky]}
                                    arrow={true}
                                    theme="aivrlight"
                                    hideOnClick={false}
                                    interactive={false}>
                                    <span
                                        className="SessionTimestamp"
                                        onClick={(e) => handleCopyTimestampClick(e)}>
                                        <FontAwesomeIcon icon={faClipboard} />
                                    </span>
                                </LazyTippy>
                            </>
                        ) : (
                            "No Route Selected"
                        )}
                    </span>
                </div>
            </div>
        </div>
    );
};

export const Datum = ({ hud = false }) => {
    const dispatch = useDispatch();
    const comparisonCoord = useSelector(comparisonCoordSelector);
    const timestamp = useSelector(timestampSelector);
    const videolessTimestamp = useSelector(videolessTimestampSelector);
    const heading = useSelector(headingSelector);
    const playlistPositionCoords = useSelector(playlistPositionCoordsSelector);
    const datumInformation = useSelector(datumSelector);
    const userConfig = useSelector(userConfigSelector);
    const routeSystemID = useSelector(routeSystemIDSelector);
    const routeID = useSelector(routeIDSelector);
    const currentRoutePosition = useSelector(currentRoutePositionSelector);
    const routeGeometryData = useSelector(routeGeomSelector);
    const speedMetadata = useSelector(speedAndBearingSelector);
    const snappingData = useSelector(snappingDataSelector);
    const routeData = useSelector(routeDetailsSelector);
    const routeLocationsV2 = useSelector(routeLocationsV2Selector);
    const sourceIndex = useSelector(sourceIndexSelector);
    const cameraOffsets = useSelector(cameraOffsetsSelector);
    const currentSessionTags = useSelector(currentSessionTagsSelector);

    const [datumCoord, setDatumCoord] = useState(datumInformation?.location || []);

    const timestampSeconds = useMemo(() => {
        return timestamp ? timestamp : videolessTimestamp / 1000;
    }, [timestamp, videolessTimestamp]);

    const cameraOffset = useMemo(() => {
        const sessionIsBackward = _.includes(currentSessionTags, "Backward");
        const currentOffset = _.get(cameraOffsets, sourceIndex, 0);
        if (sessionIsBackward) {
            return -currentOffset;
        }
        return currentOffset;
    }, [cameraOffsets, sourceIndex, currentSessionTags]);

    const datumDistanceMeters = useMemo(() => {
        if (!datumInformation) {
            return null;
        }
        const currentDistance = calculateDistanceInPlaylist(timestampSeconds, routeGeometryData, cameraOffset);
        let distanceFromDatum = currentDistance - datumInformation.playlistDistance;

        if (distanceFromDatum < 10 && distanceFromDatum > -10) {
            distanceFromDatum = Math.round(distanceFromDatum * 10) / 10;
        } else {
            distanceFromDatum = Math.round(distanceFromDatum);
        }

        return distanceFromDatum;
    }, [datumInformation, routeGeometryData, timestampSeconds, cameraOffset]);

    const distanceFromDatumInfo = useMemo(() => {
        if (!datumInformation) {
            return null;
        }

        const measurementUnit = userConfig.measurement_units;
        const distanceFromDatum = Math.abs(datumDistanceMeters);

        if (measurementUnit === "metres") {
            return `${distanceFromDatum}m from datum`;
        } else if (measurementUnit === "yards") {
            let yardValue = distanceFromDatum * 1.0936132983;
            if (yardValue < 10) {
                yardValue = Math.round(yardValue * 10) / 10;
            } else {
                yardValue = Math.round(yardValue);
            }
            return `${yardValue}y from datum`;
        }
    }, [datumInformation, userConfig, datumDistanceMeters]);

    const setDatum = useCallback(() => {
        const elr = parseRoutePositionData(currentRoutePosition, routeSystemID);
        setDatumCoord(playlistPositionCoords);
        dispatch(
            featureOverlayAddFeature(
                {
                    type: "flag",
                    heading: (180 * heading) / Math.PI,
                    location: playlistPositionCoords,
                    routePosition: elr,
                    routeInformationDatum: true,
                },
                false,
            ),
        );
        if (elr) {
            dispatch(
                customAudit(
                    "datum_placed",
                    {
                        position: `${elr.route} ${elr.mile}m ${elr.chain}c`,
                    },
                    `Datum placed - ${elr.route} ${elr.mile}m ${elr.chain}c`,
                ),
            );
        }
        dispatch(featureOverlaySetDatum(timestampSeconds, playlistPositionCoords, cameraOffset));
    }, [currentRoutePosition, routeSystemID, dispatch, heading, playlistPositionCoords, timestampSeconds, cameraOffset]);

    const removeDatum = useCallback(() => {
        dispatch(featureOverlaySetDatum(null, null));
        dispatch(featureOverlayClearFeatures());
        setDatumCoord([]);
    }, [dispatch]);

    const isDatumDisabled = useMemo(() => {
        const parsedRoutePosition = parseRoutePositionData(currentRoutePosition, routeSystemID);
        return !playlistPositionCoords || !parsedRoutePosition;
    }, [currentRoutePosition, playlistPositionCoords, routeSystemID]);

    const datumDisplay = useMemo(() => {
        let datum_text = null;
        let distance_text = "Datum not set";

        if (comparisonCoord) {
            datum_text = (
                <RouteCoordinateDisplay
                    key={`${comparisonCoord.route}.${comparisonCoord.subposition}`}
                    coordinate={comparisonCoord}
                    readonly={true}
                    hud={hud}
                />
            );
        }

        if (distanceFromDatumInfo) {
            distance_text = distanceFromDatumInfo;
        }

        const handleGoToDatum = () => {
            const coordTimestamp = coordinateToTimestampV2(datumCoord, routeLocationsV2, cameraOffset);
            updatePlaylistTime(dispatch, routeID, coordTimestamp);
        };

        const updatePlaylistTime = (dispatch, routeId, datumTimestamp) => {
            dispatch(routeSelected(routeId, datumTimestamp, undefined, undefined, undefined, 0, false, false, null, null, false));
        };

        return (
            <div className={hud ? "HUD-RouteInfoDatum" : "RouteInfoDatum"}>
                <div className={hud ? "HUD-RouteInfoDatum__offset-container" : "RouteInfoDatum__offset-container"}>
                    <div className={hud ? "HUD-RouteInfoDatum__OffsetText" : "RouteInfoDatum__OffsetText"}>
                        <span className="secondary">{distance_text}</span>
                    </div>
                    <div
                        size="small"
                        disabled={isDatumDisabled}
                        className={hud ? "OverlayButton active full" : "ant-btn RouteInfoDatum__DatumButton ant-btn-sm"}
                        onClick={() => {
                            if (!isDatumDisabled) {
                                setDatum();
                            }
                        }}
                        title={!currentRoutePosition || _.isEmpty(currentRoutePosition) ? "ELR data missing, unable to set Datum" : false}>
                        Set Here
                    </div>

                    {datumInformation && (
                        <>
                            <div
                                size="small"
                                disabled={!playlistPositionCoords}
                                className={hud ? "OverlayButton active full" : "ant-btn RouteInfoDatum__DatumButton ant-btn-sm"}
                                onClick={handleGoToDatum}>
                                Go To Datum
                            </div>
                            <div
                                size="small"
                                disabled={!playlistPositionCoords}
                                className={hud ? "OverlayButton active full" : "ant-btn RouteInfoDatum__DatumButton ant-btn-sm"}
                                onClick={removeDatum}>
                                Clear Datum
                            </div>
                        </>
                    )}
                </div>
                {datum_text && (
                    <div className={hud ? "HUD-RouteInfoDatum__datum-container" : "RouteInfoDatum__datum-container"}>
                        <span className="DatumLabel">Current datum</span>
                        {datum_text}
                    </div>
                )}
            </div>
        );
    }, [
        comparisonCoord,
        distanceFromDatumInfo,
        hud,
        isDatumDisabled,
        currentRoutePosition,
        datumInformation,
        playlistPositionCoords,
        removeDatum,
        dispatch,
        routeID,
        setDatum,
        routeLocationsV2,
        cameraOffset,
        datumCoord,
    ]);

    const speedValue = useMemo(() => {
        const speedInfo = findMostRecentMetadata(speedMetadata, timestampSeconds);

        let speed = null;

        if (speed == null && !_.isNil(speedInfo?.data.speed) && speedInfo.timestamp > timestampSeconds - 5) {
            speed = speedInfo?.data.speed;
            speed = Math.round(convertSpeed(speed, "miles"));
        }

        return speed;
    }, [speedMetadata, timestampSeconds]);

    const copyTestData = useCallback(() => {
        const date = new Date(timestamp * 1000);
        const dateString = date.getDate() + "/" + (date.getMonth() + 1) + "/" + date.getFullYear();

        const trackID = _.get(currentRoutePosition, "subposition");

        const elr = parseRoutePositionData(currentRoutePosition, routeSystemID);
        let positionString = "";
        if (elr) {
            positionString = elr.to_string("elr_mile_yards");
        }

        const mapMatcherVersion = _.get(snappingData, ["mapSnapperVersion"], "");
        const networkModel = _.get(snappingData, ["snappingSystem"], "");
        const networkModelVersion = _.get(snappingData, ["inmVersion"], "");
        const deviceName = _.get(routeData, ["device_name"], "");

        const sessionLink = `https://view.aivr.video/view/${routeID}${timestamp ? "/" + timestamp * 1000 : ""}`;
        const dataValues = [
            routeID,
            dateString,
            deviceName,
            datumDistanceMeters,
            mapMatcherVersion,
            networkModel,
            networkModelVersion,
            trackID,
            speedValue,
            positionString,
            sessionLink,
        ];

        const copyString = dataValues.join("\t");
        navigator.clipboard.writeText(copyString);
    }, [currentRoutePosition, datumDistanceMeters, routeData, routeID, routeSystemID, snappingData, speedValue, timestamp]);

    return (
        <div className={hud ? "HUD-RouteInfo-Altitude" : "RouteInfo-Altitude"}>
            <div className={hud ? "HUD-RouteInfo-Datum-Container" : "RouteInfo-Text-Container"}>{datumDisplay}</div>
            {distanceFromDatumInfo && !hud && userConfig.super_admin && (
                <button
                    className="CopyDatumDebugButton"
                    onClick={copyTestData}>
                    Copy Test Data
                </button>
            )}
        </div>
    );
};

const playlistPositionCoordsSelector = (state) => state.playlist.position.coords;
const routeCoordDataSelector = (state) => state.playlist.position.routeCoordData;
const userConfigSelector = (state) => state.userDetails.userConfig;
const currentDashboardSelector = (state) => _.find(state.dashboards, (dash) => dash.access_id === state.userDetails.dashboardAccessID);
const datumOffsetsSelector = (state) => {
    const currentDashboard = _.find(state.dashboards, (dash) => dash.access_id === state.userDetails.dashboardAccessID);
    const userViewOffsets = _.get(state.views, [state.userDetails.userConfig.view_id, "datum_offsets"], []);
    const workspaceViewOffsets = _.get(state.views, [_.get(currentDashboard, ["config", "view_id"], -1), "datum_offsets"], []);
    return workspaceViewOffsets.length ? workspaceViewOffsets : userViewOffsets;
};
const validatedSnappingSelector = (state) => state.playlist.position.snappingValidations;
const comparisonCoordSelector = (state) => _.get(state.featureOverlay.datum, "routePosition");

const validationLoadingIcon = (
    <div className="elrSpinnerContainer">
        <Spin className="elrValidationSpinner" />
    </div>
);

const notValidatedIcon = (
    <LazyTippy
        placement="right"
        sticky={true}
        plugins={[sticky]}
        arrow={true}
        theme="aivrlight"
        interactive="true"
        content="Click to validate ELR">
        <div>
            <FontAwesomeIcon
                icon={faCheck}
                style={{ marginLeft: 7.5 }}
                className={"snapValidationIcon"}
            />
        </div>
    </LazyTippy>
);

const validatedIcon = (
    <LazyTippy
        placement="right"
        sticky={true}
        plugins={[sticky]}
        arrow={true}
        theme="aivrlight"
        interactive="true"
        content="ELR Validated">
        <div>
            <FontAwesomeIcon
                icon={faCheck}
                style={{ marginLeft: 7.5 }}
                className={"snapValidationIcon validated"}
            />
        </div>
    </LazyTippy>
);

const copyToClipboardIcon = (
    <LazyTippy
        placement="top"
        sticky={true}
        plugins={[sticky]}
        arrow={true}
        theme="aivrlight"
        interactive="true"
        content="Copy to clipboard">
        <div>
            <FontAwesomeIcon
                icon={faClipboard}
                style={{ marginLeft: 7.5 }}
            />
        </div>
    </LazyTippy>
);

const playerStateSelector = (state) => _.get(state.playlist, ["position", "playerState"], "paused");

function RouteCoordinateDisplay({ coordinate, readonly = false, estimate = false, setDisplayEstimates, triggerCoordinateLookup, hud = false }) {
    const dispatch = useDispatch();
    const validatedSnapping = useSelector(validatedSnappingSelector);
    const userConfig = useSelector(userConfigSelector);
    const datum_offsets = useSelector(datumOffsetsSelector);
    const routeID = useSelector(routeIDSelector);
    const isFullscreen = useSelector(fullscreenSelector);
    const playerState = useSelector(playerStateSelector);

    const [elrValidationLoading, setElrValidationLoading] = useState();

    const ELRCopyString = useMemo(() => coordinate.to_string(userConfig.elr_units, datum_offsets), [coordinate, userConfig.elr_units, datum_offsets]);

    const validationIcon = useMemo(() => {
        const validatedIndex = _.findIndex(validatedSnapping, (snapping) => {
            return (
                snapping.routeID === coordinate.route &&
                Math.abs(snapping.routePosition - coordinate.position) <= 1 &&
                snapping.routeSubPosition === coordinate.subposition
            );
        });
        const elrValidated = validatedIndex > -1;
        const validatedID = elrValidated ? validatedSnapping[validatedIndex].id : -1;

        if (!readonly) {
            if (elrValidationLoading) {
                return validationLoadingIcon;
            } else if (elrValidated && userConfig.super_admin) {
                return (
                    <div
                        onClick={() => {
                            setElrValidationLoading(true);
                            dispatch(
                                deleteSnappingValidation(validatedID, () => {
                                    setElrValidationLoading(false);
                                }),
                            );
                        }}>
                        {validatedIcon}
                    </div>
                );
            } else {
                if (estimate) {
                    if (userConfig.super_admin) {
                        return (
                            <div
                                onClick={() => {
                                    setElrValidationLoading(true);
                                    dispatch(
                                        validateSnapping(routeID, coordinate.system, coordinate.route, coordinate.position, coordinate.subposition, () => {
                                            setElrValidationLoading(false);
                                        }),
                                    );
                                }}>
                                {notValidatedIcon}
                            </div>
                        );
                    }
                } else if (playerState !== "playing") {
                    return (
                        <div
                            onClick={() => {
                                triggerCoordinateLookup();
                                setDisplayEstimates(true);
                            }}
                            className="elrIncorrectButton">
                            Show more...
                        </div>
                    );
                }
            }
        } else {
            return null;
        }
    }, [
        userConfig,
        coordinate,
        elrValidationLoading,
        dispatch,
        routeID,
        validatedSnapping,
        readonly,
        estimate,
        setDisplayEstimates,
        playerState,
        triggerCoordinateLookup,
    ]);

    const ELRInfo = [];

    const hasValidSource = useMemo(() => _.isArray(coordinate.sources) && coordinate.sources.length > 0, [coordinate]);

    _.map(coordinate.displayFields(userConfig.elr_units), (field_name) => {
        if (!_.isNil(coordinate.field_value(field_name, datum_offsets))) {
            ELRInfo.push({
                name: field_name,
                value: coordinate.field_value(field_name, datum_offsets),
            });
        }
    });

    if (hud) {
        return (
            <div
                className={"HUD-elrRow"}
                key={`${coordinate.route}.${coordinate.subposition}`}>
                <div className={"HUD-ELRContainer"}>
                    <div className={"HUD-ELRInfo"}>
                        {ELRInfo.map((field, index) => {
                            return (
                                <>
                                    <div className="HUD-ELRInfo-Row">
                                        <span className="name-label">{field.name}</span>
                                        <span>{field.value}</span>
                                    </div>
                                </>
                            );
                        })}
                    </div>
                    <div className="HUD-CoordinateTools">
                        {hasValidSource && (
                            <LazyTippy
                                placement="top"
                                sticky={true}
                                plugins={[sticky]}
                                arrow={true}
                                theme="aivrlight"
                                interactive="true"
                                allowHTML={true}
                                content={
                                    <div className="ElrSources">
                                        <p className="Header">Location Sources:</p>
                                        {coordinate.sources.map((source) => {
                                            return (
                                                <p
                                                    className="Source"
                                                    key={source}>
                                                    {source}
                                                </p>
                                            );
                                        })}
                                    </div>
                                }>
                                <div className="CoordinateSources">
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        style={{ marginLeft: 7.5 }}
                                    />
                                </div>
                            </LazyTippy>
                        )}
                        <div
                            className="CopyToClipboard"
                            onClick={() => {
                                navigator.clipboard.writeText(ELRCopyString);
                            }}>
                            {copyToClipboardIcon}
                        </div>
                    </div>
                </div>

                {hasValidSource && coordinate.sources.includes("MWV") && (
                    <div className="elrRow__logo">
                        <img
                            src={mwvLogo}
                            alt="Machines With Vision Logo"
                            crossOrigin={"anonymous"}
                        />
                    </div>
                )}

                {!isFullscreen && validationIcon}
            </div>
        );
    } else {
        return (
            <div
                className="elrRow"
                key={`${coordinate.route}.${coordinate.subposition}`}>
                {ELRInfo.map((field) => {
                    return (
                        <>
                            <span
                                key={field.name}
                                className="RouteInfoELR__Item">
                                <b>{field.name}:</b> <span className="secondary">{field.value}</span>
                            </span>
                        </>
                    );
                })}

                {hasValidSource && coordinate.sources.includes("MWV") && (
                    <div className="elrRow__logo">
                        <img
                            src={mwvLogo}
                            alt="Machines With Vision Logo"
                            crossOrigin={"anonymous"}
                        />
                    </div>
                )}

                {hasValidSource && (
                    <LazyTippy
                        placement="top"
                        sticky={true}
                        plugins={[sticky]}
                        arrow={true}
                        theme="aivrlight"
                        interactive="true"
                        allowHTML={true}
                        content={
                            <div className="ElrSources">
                                <p className="Header">Location Sources:</p>
                                {coordinate.sources.map((source) => {
                                    return (
                                        <p
                                            className="Source"
                                            key={source}>
                                            {source}
                                        </p>
                                    );
                                })}
                            </div>
                        }>
                        <div className="CoordinateSources">
                            <FontAwesomeIcon
                                icon={faInfoCircle}
                                style={{ marginLeft: 7.5 }}
                            />
                        </div>
                    </LazyTippy>
                )}
                <div
                    className="CopyToClipboard"
                    onClick={() => {
                        navigator.clipboard.writeText(ELRCopyString);
                    }}>
                    {copyToClipboardIcon}
                </div>
                {!isFullscreen && validationIcon}
            </div>
        );
    }
}

const videolessTimestampSelector = (state) => {
    if (_.get(state, ["playlist", "data", "video", "length"], 0)) {
        return null;
    }
    const currentIndex = state.playlist.position.currentIndex;
    const railImages = state.railInspection.railInspectionImages.data;

    let timestamp = 0;
    if (railImages[currentIndex]) {
        const currentImage = railImages[currentIndex];
        timestamp = currentImage.timestamp;
    }
    return timestamp;
};

function CoordinatesForSystem({ system, classes, hud }) {
    const timestamp = useSelector(timestampSelector);
    const playlistPositionCoords = useSelector(playlistPositionCoordsSelector);
    const routeCoordData = useSelector(routeCoordDataSelector);
    const reduxRoutePos = useSelector(currentRoutePositionSelector);
    const [displayEstimates, setDisplayEstimates] = useState(false);

    const [loadingCoordinates, setCoordinatesLoading] = useState(false);

    const dispatch = useDispatch();

    useEffect(() => {
        setDisplayEstimates(false);
    }, [timestamp]);

    const toggleDisplayEstimates = (value) => {
        setDisplayEstimates(value);
    };

    const currentCoords = useMemo(() => {
        if (playlistPositionCoords && playlistPositionCoords[0] !== null && playlistPositionCoords[1] !== null) {
            return playlistPositionCoords;
        } else {
            return null;
        }
    }, [playlistPositionCoords]);

    const groupedDataForSystem = useMemo(() => {
        const retVal = {};
        if (system && system.ID) {
            retVal[system.ID] = _.get(routeCoordData, [system.ID], []);
        }
        return retVal;
    }, [routeCoordData, system]);

    const routeCoordinatesForSystem = useMemo(() => {
        if (!displayEstimates && reduxRoutePos && reduxRoutePos.position !== null && reduxRoutePos.routeID !== null) {
            const reduxSystem = system.create(reduxRoutePos.routeID, reduxRoutePos.position, reduxRoutePos.subposition);

            return [reduxSystem];
        } else {
            if (!currentCoords || !system) {
                return [];
            }
            return calculateELREstimates(currentCoords, groupedDataForSystem, [system.ID]);
        }
    }, [displayEstimates, reduxRoutePos, currentCoords, system, groupedDataForSystem]);

    useEffect(() => {
        if (routeCoordinatesForSystem.length) {
            setCoordinatesLoading(false);
        }
    }, [routeCoordinatesForSystem]);

    const triggerCoordinateLookup = useCallback(() => {
        setCoordinatesLoading(true);
        dispatch(getRouteSystemCoords(currentCoords));
    }, [currentCoords, dispatch]);

    let routePositionItems = useMemo(() => {
        if (routeCoordinatesForSystem && routeCoordinatesForSystem.length) {
            return routeCoordinatesForSystem.map((coordinateSystem) => {
                if (!coordinateSystem) {
                    return null;
                } else {
                    return (
                        <RouteCoordinateDisplay
                            key={`${coordinateSystem.route}.${coordinateSystem.subposition}`}
                            coordinate={coordinateSystem}
                            estimate={displayEstimates}
                            setDisplayEstimates={toggleDisplayEstimates}
                            triggerCoordinateLookup={triggerCoordinateLookup}
                            hud={hud}
                        />
                    );
                }
            });
        } else {
            return false;
        }
    }, [routeCoordinatesForSystem, displayEstimates, triggerCoordinateLookup, hud]);

    if (!routePositionItems && !loadingCoordinates) {
        return <span className="secondary">No location data</span>;
    } else if (!routePositionItems && loadingCoordinates) {
        routePositionItems = (
            <div className="elrSpinnerContainer">
                <Spin className="elrValidationSpinner" />
            </div>
        );
    }

    return <div className={hud ? "HUD-RouteInfoELR__ContainerList" : "RouteInfoELR__ContainerList"}>{routePositionItems}</div>;
}

export function SystemCoordinates({ classes, hud = false }) {
    const routeIsLoaded = useSelector(routeIsLoadedSelector);
    const routeSystemID = useSelector(routeSystemIDSelector);

    const coordinates = useMemo(() => {
        const coordSystemToUse = _.find(RouteCoordinateSystems, (system) => routeSystemID === system.ID);
        if (coordSystemToUse) {
            return (
                <CoordinatesForSystem
                    classes={classes}
                    key={coordSystemToUse.ID}
                    system={coordSystemToUse}
                    hud={hud}
                />
            );
        }
        return null;
    }, [routeSystemID, classes, hud]);

    if (routeIsLoaded) {
        return <>{coordinates && coordinates}</>;
    } else {
        return <span className="secondary">No Route Selected</span>;
    }
}

const EnvironmentDataPanel = () => {
    const sessionEnvironmentData = useSelector(environmentDataSelector);

    const routeSystemID = useSelector(routeSystemIDSelector);
    const routeIsLoaded = useSelector(routeIsLoadedSelector);
    const currentRouteLocation = useSelector(currentRoutePositionSelector);

    const envDataToDisplay = useMemo(() => {
        const location = parseRoutePositionData(currentRouteLocation, routeSystemID);

        const envDatas = findEnvironmentalData(sessionEnvironmentData, location);
        if (!envDatas.length) {
            return (
                <div className="EnvDatasContainer">
                    <span>No Data Available</span>
                </div>
            );
        }

        const groupedDatas = _.groupBy(envDatas, "display_data_type");

        return (
            <div className="EnvDatasContainer">
                {Object.keys(groupedDatas).map((groupName) => {
                    const datas = groupedDatas[groupName];
                    return (
                        <div className="EnvDataContainer">
                            <span className="GroupHeader">{groupName}</span>
                            {datas.flatMap((dataObj) => {
                                return Object.keys(dataObj.display_fields).map((field) => {
                                    return (
                                        <div className="EnvDataContainerInfo">
                                            <span className="Header">{field}:</span>
                                            <span className="Value">{dataObj.display_fields[field]}</span>
                                        </div>
                                    );
                                });
                            })}
                        </div>
                    );
                })}
            </div>
        );
    }, [currentRouteLocation, routeSystemID, sessionEnvironmentData]);

    if (!routeIsLoaded) {
        return <span className="secondary">{"No Route Selected"}</span>;
    }

    return <div>{envDataToDisplay}</div>;
};

export const WeatherStationPanel = ({ weatherData, hud = false }) => {
    const elements = useMemo(() => {
        let elements = [];

        if (weatherData) {
            if (!hud && weatherData.name) {
                elements.push({
                    name: weatherData.name,
                    value: null,
                    icon: faHome,
                });
            }

            if (weatherData.air_temp_c) {
                elements.push({
                    name: "Temperature",
                    value: weatherData.air_temp_c + "°C",
                    icon: faTemperatureHigh,
                });
            }
            if (weatherData.rainfall_last_hour_mm) {
                elements.push({
                    name: "Rainfall",
                    value: weatherData.rainfall_last_hour_mm + "mm",
                    icon: faCloudRain,
                });
            }

            if (weatherData.w_dir) {
                const directions = {
                    N: "North",
                    NNE: "North-Northeast",
                    NE: "Northeast",
                    ENE: "East-Northeast",
                    E: "East",
                    ESE: "East-Southeast",
                    SE: "Southeast",
                    SSE: "South-Southeast",
                    S: "South",
                    SSW: "South-Southwest",
                    SW: "Southwest",
                    WSW: "West-Southwest",
                    W: "West",
                    WNW: "West-Northwest",
                    NW: "Northwest",
                    NNW: "North-Northwest",
                };
                elements.push({
                    name: "Wind Direction",
                    value: _.get(directions, weatherData.w_dir, weatherData.w_dir),
                    icon: faWindsock,
                });
            }
        }
        return elements;
    }, [hud, weatherData]);

    return hud ? (
        <div className="HUD-WeatherData">
            {elements.map((element) => {
                return (
                    <div className="HUD-WeatherData-Row">
                        <div>
                            <FontAwesomeIcon icon={element.icon} />
                        </div>
                        <span>{element.value}</span>
                    </div>
                );
            })}
        </div>
    ) : (
        <div
            className="RouteInfo-Row"
            key="weather">
            {!!elements.length &&
                _.map(elements, (element) => {
                    return (
                        <div className="RouteInfo-Altitude">
                            <div className="RouteInfo-Text-Container">
                                <Tippy
                                    arrow={true}
                                    placement="top"
                                    content={element.name}
                                    theme="aivrlight"
                                    zIndex="600">
                                    <div
                                        className={`RouteInfo-BlockItem ${element.name}`}
                                        style={!element.value ? { gap: 0 } : {}}>
                                        <FontAwesomeIcon icon={element.icon} />
                                        <span className="secondary">{element.value}</span>
                                    </div>
                                </Tippy>
                            </div>
                        </div>
                    );
                })}
        </div>
    );
};

const coordsSelector = (state) => state.playlist.position.coords;
const userIsSuperAdminSelector = (state) => state.userDetails.userConfig.super_admin;

const FIFTEEN_MINUTES_IN_SECONDS = 900;

const RouteInformation = () => {
    const dispatch = useDispatch();
    const openInfoPanels = useSelector(openInfoPanelsSelector);
    const isFullscreen = useSelector(fullscreenSelector);
    const [unitSelectionModalVisible, setUnitSelectionModalVisible] = useState();
    const routeID = useSelector(routeIDSelector);
    const reduxCoords = useSelector(coordsSelector);
    const weatherRequestedTimestamp = useSelector(weatherRequestedTimestampSelector);
    const [refreshingWeatherData, setRefreshingWeatherData] = useState(false);
    const weatherData = useSelector(weatherDataSelector);
    const weatherCoords = useSelector(weatherCoordsSelector);
    const currentTs = useSelector(timestampSelector);
    const currentDashboard = useSelector(currentDashboardSelector);
    const userIsSuperAdmin = useSelector(userIsSuperAdminSelector);
    const currentRoutePosition = useSelector(currentRoutePositionSelector);

    const [responsibleAreas, setResponsibleAreas] = useState(null);

    const pullWeatherData = useCallback(
        (coords, newTs) => {
            setRefreshingWeatherData(true);
            dispatch(
                getSessionWeatherData(newTs, coords[1], coords[0], () => {
                    setRefreshingWeatherData(false);
                }),
            );
        },
        [dispatch],
    );

    useEffect(() => {
        const [lon, lat] = reduxCoords || [];
        if (lon && lat) {
            // this to be run if routeID change
            if (routeID && currentTs) {
                const differenceInSeconds = Math.abs(currentTs - weatherRequestedTimestamp);
                if (differenceInSeconds > FIFTEEN_MINUTES_IN_SECONDS && !_.isEqual(reduxCoords, weatherCoords)) {
                    pullWeatherData(reduxCoords, currentTs);
                }
            }
        } else {
            dispatch(clearWeatherData());
        }
    }, [currentTs, dispatch, pullWeatherData, reduxCoords, routeID, weatherCoords, weatherRequestedTimestamp]);

    // if session is closed
    useEffect(() => {
        if (!routeID) {
            dispatch(clearWeatherData());
        }
        setResponsibleAreas(null);
    }, [dispatch, routeID]);

    useEffect(() => {
        if (responsibleAreas !== null) {
            setResponsibleAreas(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentRoutePosition]);

    const formattedResponsibleAreas = useMemo(() => {
        if (!_.isObject(responsibleAreas)) {
            return null;
        }

        return (
            <div className="ResponsibleAreaTooltip">
                {Object.entries(responsibleAreas).map(([label, value]) => {
                    return (
                        <span>
                            <span className="ResponsibleAreaTooltip-Label">{label}</span>: {value}
                        </span>
                    );
                })}
            </div>
        );
    }, [responsibleAreas]);

    const showUnitSelectionIcon = useMemo(() => {
        if (!isFullscreen) {
            return (
                <div style={{ display: "flex", gap: 5 }}>
                    <span>ROUTE POSITION (ESTIMATE)</span>
                    <Tippy
                        placement="top"
                        sticky={true}
                        plugins={[sticky]}
                        arrow={true}
                        theme="aivrlight"
                        content="Change Unit System">
                        <div>
                            <FontAwesomeIcon
                                className="RouteInfo-ELRSettings__Icon"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setUnitSelectionModalVisible(true);
                                }}
                                icon={faCog}
                            />
                        </div>
                    </Tippy>

                    {userIsSuperAdmin && routeID && (
                        <Tippy
                            placement="top"
                            sticky={true}
                            plugins={[sticky]}
                            arrow={true}
                            theme="aivrlight"
                            content={formattedResponsibleAreas || "Responsible Areas"}
                            trigger="mouseenter click">
                            <div>
                                <FontAwesomeIcon
                                    className="RouteInfo-ELRSettings__Icon no-spin"
                                    onClick={(e) => {
                                        e.stopPropagation();

                                        if (formattedResponsibleAreas === null) {
                                            dispatch(getNetworkTrackResponsibility()).then((response) => {
                                                setResponsibleAreas(response.responsible_areas);
                                            });
                                        }
                                    }}
                                    icon={faCompassSolid}
                                />
                            </div>
                        </Tippy>
                    )}
                </div>
            );
        } else {
            return <span>ROUTE POSITION (ESTIMATE)</span>;
        }
    }, [setUnitSelectionModalVisible, isFullscreen, routeID, userIsSuperAdmin, formattedResponsibleAreas]);

    const showWeatherUpdatingIcon = useMemo(() => {
        if (!isFullscreen) {
            return (
                <div style={{ display: "flex", gap: 5 }}>
                    <span>WEATHER STATION</span>
                    {weatherData && (
                        <Tippy
                            placement="top"
                            sticky={true}
                            plugins={[sticky]}
                            arrow={true}
                            theme="aivrlight"
                            content={refreshingWeatherData ? "Updating data..." : "Click to update"}>
                            <div>
                                <FontAwesomeIcon
                                    spin={refreshingWeatherData}
                                    className="RouteInfo-ELRSettings__Icon"
                                    icon={faSyncAlt}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        pullWeatherData(reduxCoords, currentTs);
                                    }}
                                />
                            </div>
                        </Tippy>
                    )}
                </div>
            );
        }
    }, [isFullscreen, weatherData, refreshingWeatherData, pullWeatherData, reduxCoords, currentTs]);

    const datumHeader = useMemo(() => {
        return <span className="RouteInfo-Label">DATUM</span>;
    }, []);

    const infoPanelChange = useCallback(
        (openPanels) => {
            dispatch(setUserPreference("openInfoPanels", openPanels));
        },
        [dispatch],
    );

    const activeKeys = useMemo(() => {
        if (openInfoPanels && openInfoPanels.length) {
            return openInfoPanels;
        } else {
            return ["1", "2", "3", "4", "5", "6", "7"];
        }
    }, [openInfoPanels]);

    const environmentTabEnabled = useMemo(() => {
        const enabled = _.get(currentDashboard, ["config", "environmental_data_enabled"], false);
        return enabled;
    }, [currentDashboard]);

    return (
        <React.Fragment>
            <RouteTitleDetails />
            {unitSelectionModalVisible && <UnitSelectionModal onClose={() => setUnitSelectionModalVisible(false)} />}
            <Collapse
                defaultActiveKey={activeKeys}
                ghost
                onChange={infoPanelChange}>
                <Panel
                    header={showUnitSelectionIcon}
                    key="1">
                    <SystemCoordinates unitSelectionModalVisible={unitSelectionModalVisible} />
                </Panel>
                <Panel
                    header="SPEED, BEARING & ALTITUDE"
                    key="2">
                    <PositionalInfo />
                </Panel>
                <Panel
                    header="TIME & DATE"
                    key="time">
                    <VideoTimeDisplay />
                </Panel>
                {weatherData && (
                    <Panel
                        header={showWeatherUpdatingIcon}
                        key="7">
                        <WeatherStationPanel
                            weatherData={weatherData}
                            routeID={routeID}
                        />
                    </Panel>
                )}
                {environmentTabEnabled && (
                    <Panel
                        header="ENVIRONMENTAL DATA"
                        key="8">
                        <EnvironmentDataPanel />
                    </Panel>
                )}
                <Panel
                    header={
                        <div style={{ display: "flex", gap: 5 }}>
                            <span>DETECTIONS</span>
                        </div>
                    }
                    key="3">
                    <MarkerPanel />
                </Panel>
                <Panel
                    header="POSITION"
                    key="4"
                    style={{ position: "relative" }}>
                    <LatLonInfo />
                </Panel>
                <Panel
                    header={datumHeader}
                    key="5">
                    <Datum />
                </Panel>
            </Collapse>
        </React.Fragment>
    );
};

export default RouteInformation;
